import { apiErrorMessage } from '@/api/client';
import { categoriesApi } from '@/api/categories.api';
import { GHOST_BUTTON, INPUT } from '@/components/admin/ui';
import { Modal } from '@/components/common/Modal';
import { CheckIcon, CloseIcon } from '@/components/common/icons';
import type { CategoryModel, PaletteColorModel } from '@/types/categories.types';
import type { FC } from 'react';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface AddCategoryModalProps {
  categories: CategoryModel[];
  onCreate: (label: string, color: string) => Promise<unknown>;
  onClose: () => void;
}

const LABEL = 'text-[11.5px] font-semibold text-muted';

/**
 * The prototype's "Přidat vlastní kategorii": a name (≥ 3 characters, unique ignoring case) and a
 * colour from the palette colours no category uses yet (`GET /categories/palette`).
 */
export const AddCategoryModal: FC<AddCategoryModalProps> = ({ categories, onCreate, onClose }) => {
  const { t } = useTranslation();
  const titleId = useId();
  const [label, setLabel] = useState('');
  const [palette, setPalette] = useState<PaletteColorModel[]>([]);
  const [picked, setPicked] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    categoriesApi
      .palette()
      .then(setPalette)
      .catch((err: unknown) => setError(apiErrorMessage(err, t('admin.loadFailed'))));
  }, [t]);

  const free = palette.filter((entry) => entry.free);
  const color = free.find((entry) => entry.color === picked)?.color ?? free[0]?.color ?? null;
  const trimmed = label.trim();
  const taken = categories.some((entry) => entry.label_cs.trim().toLowerCase() === trimmed.toLowerCase());
  const ok = trimmed.length >= 3 && !taken && color !== null;

  const hint =
    error ??
    (trimmed.length < 3
      ? t('admin.addCategoryDialog.hintShort')
      : taken
        ? t('admin.categories.nameTaken')
        : t('admin.addCategoryDialog.hintOk'));

  const submit = async () => {
    if (!ok || !color) return;
    setIsSaving(true);
    setError(null);
    try {
      await onCreate(trimmed, color);
      onClose();
    } catch (err) {
      setError(apiErrorMessage(err, t('admin.saveFailed')));
      setIsSaving(false);
    }
  };

  return (
    <Modal onClose={onClose} maxWidth={470} labelledBy={titleId} className="flex flex-col gap-3.5 px-6 pb-5 pt-6">
      <div className="flex items-baseline gap-3">
        <div id={titleId} className="text-base font-bold text-primary">
          {t('admin.addCategoryDialog.title')}
        </div>
        <span className="flex-1" />
        <button
          type="button"
          onClick={onClose}
          aria-label={t('admin.addCategoryDialog.close')}
          className="flex h-[26px] w-[26px] cursor-pointer items-center justify-center self-center rounded border border-control bg-white text-muted hover:border-primary hover:text-primary"
        >
          <CloseIcon size={13} />
        </button>
      </div>
      <label className="flex flex-col gap-[5px]">
        <span className={LABEL}>{t('admin.addCategoryDialog.name')}</span>
        <input
          value={label}
          onChange={(event) => {
            setLabel(event.target.value);
            setError(null);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') submit();
          }}
          placeholder={t('admin.addCategoryDialog.namePlaceholder')}
          maxLength={80}
          // biome-ignore lint/a11y/noAutofocus: the dialog exists to type this name
          autoFocus={true}
          className={`${INPUT} w-full px-[11px] py-[9px] text-[12.5px]`}
        />
      </label>
      <div className="flex flex-col gap-[7px]">
        <div className={LABEL}>{t('admin.addCategoryDialog.color')}</div>
        <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label={t('admin.addCategoryDialog.color')}>
          {free.map((entry) => {
            const on = entry.color === color;
            return (
              <button
                key={entry.color}
                type="button"
                role="radio"
                aria-checked={on}
                aria-label={entry.color}
                onClick={() => setPicked(entry.color)}
                className="flex h-[30px] w-[30px] cursor-pointer items-center justify-center rounded border-none p-0"
                style={{ background: entry.color, boxShadow: on ? `0 0 0 2px #fff, 0 0 0 4px ${entry.color}` : 'none' }}
              >
                {on ? <CheckIcon size={12} strokeWidth={3.5} stroke="#fff" /> : null}
              </button>
            );
          })}
        </div>
        <div className="text-[11px] text-faint">
          {palette.length && !free.length
            ? t('admin.addCategoryDialog.noColor')
            : t('admin.addCategoryDialog.colorNote')}
        </div>
      </div>
      <div className={`text-[11.5px] leading-[1.5] ${error ? 'text-danger' : 'text-label-2'}`} aria-live="polite">
        {hint}
      </div>
      <div className="flex gap-2.5">
        <button
          type="button"
          onClick={submit}
          disabled={!ok || isSaving}
          className={`flex flex-1 items-center justify-center gap-[7px] rounded border-none px-3.5 py-2.5 text-[12.5px] font-semibold ${
            ok
              ? 'cursor-pointer bg-primary text-white hover:bg-primary-deep'
              : 'cursor-not-allowed bg-control text-faint'
          }`}
        >
          {t('admin.addCategoryDialog.submit')}
        </button>
        <button type="button" onClick={onClose} className={`${GHOST_BUTTON} py-2.5`}>
          {t('admin.addCategoryDialog.cancel')}
        </button>
      </div>
    </Modal>
  );
};
